import * as PXBColors from '@brightlayer-ui/colors';
import { DeleteForever } from '@mui/icons-material';
import EditIcon from '@mui/icons-material/Edit';
import { Button, Grid, ListItemIcon, ListItemSecondaryAction } from '@mui/material';
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { Thunks } from '../../actions';
import { StarfishDeviceActions } from '../../actions/actions/starfish-device-actions';
import deviceStyles from '../../app/device-management/DeviceStyle';
import { SERIAL_TYPE, TCP_TYPE } from '../../constants';
import { useToasts } from '../../data/DataProviders/ToastProvider';
import { pushAppRoute } from '../../data/domain/route-manager';
import { useCurrentAppRoute } from '../../hooks/app-route-hooks';
import { AppRoute } from '../../routes';
import { Selectors } from '../../selectors';
import { Status } from '../../types';
import { StarfishChildDeviceData } from '../../types/device';
import { useAuthHelper } from '../AuthContextProvider';
import { DeleteConfirmModal } from '../CommonDeleteModals/DeleteConfirmModal';
import { ContentContainer } from '../ContentContainer';
import { LabelWithSubTitle } from '../LabelWithSubTitle/LabelWithSubtitle';
import { NavigationHeader } from '../NavigationHeader/NavigationHeader';
import { StatusTag } from '../StatusTag/StatusTag';

export const StarFishChildDeviceProfileData: React.FC<{
    starfishDeviceDetails: StarfishChildDeviceData | null;
    backAppRoute: AppRoute;
}> = (props) => {
    const classes = deviceStyles();
    const [pxChildDeviceInfo, setPxChildDeviceInfo] = useState<StarfishChildDeviceData | null>(null);
    const [deviceStatus, setDeviceStatus] = React.useState<boolean | null>();
    // const realTimeSiteData = useSignalRSiteData();
    const history = useHistory();
    const dispatch = useDispatch();
    const currentAppRoute = useCurrentAppRoute();
    const { addToast } = useToasts();
    const [openDelete, setOpenDelete] = React.useState(false);
    const authHelper = useAuthHelper();
    const childDeviceData = useSelector(Selectors.StarfishDevice.deviceRegistration);

    React.useEffect(() => {
        if (props.starfishDeviceDetails) {
            setPxChildDeviceInfo(props.starfishDeviceDetails);
            setDeviceStatus(props.starfishDeviceDetails.status);
        }
    }, [props.starfishDeviceDetails]);

    const handleEditDialogOpen = (): void => {
        if (currentAppRoute.type === 'StarfishChildDeviceProfile')
            pushAppRoute(history, {
                type: 'EditStarfishChildDevice',
                deviceId: currentAppRoute.deviceId,
                locationId: currentAppRoute.locationId,
                orgId: currentAppRoute.orgId,
                siteId: currentAppRoute.siteId,
                childDeviceId: currentAppRoute.childDeviceId,
            });
    };
    const navigateToDeviceProfile = (): void => {
        if (currentAppRoute.type === 'StarfishChildDeviceProfile')
            pushAppRoute(history, {
                type: 'StarfishDeviceProfile',
                orgId: currentAppRoute.orgId,
                locationId: currentAppRoute.locationId,
                siteId: currentAppRoute.siteId,
                deviceId: currentAppRoute.deviceId,
            });
    };
    React.useEffect(() => {
        if (childDeviceData.deleteChildDeviceSuccess) {
            if (props.starfishDeviceDetails && props.starfishDeviceDetails.name) {
                addToast(`${props.starfishDeviceDetails.name} deleted successfully`);
            }
            navigateToDeviceProfile();
            dispatch(StarfishDeviceActions.StarfishChildDeviceRegistrationUnmounted());
        }
    }, [childDeviceData.deleteChildDeviceSuccess]);

    const handleClose = (): void => {
        setOpenDelete(false);
    };
    const onDelete = (): void => {
        setOpenDelete(true);
    };

    const handleDelete = (): void => {
        if (props.starfishDeviceDetails) {
            if (props.starfishDeviceDetails.deviceId) {
                dispatch(
                    Thunks.StarfishChildDevice.DeleteStarfishChildDevice(
                        props.starfishDeviceDetails.deviceId,
                        authHelper,
                        addToast
                    )
                );
                setOpenDelete(false);
            }
        }
    };

    return (
        <>
            {pxChildDeviceInfo && (
                <>
                    <NavigationHeader
                        siteName={pxChildDeviceInfo.siteName}
                        for="device"
                        connectedDevice=""
                        device={pxChildDeviceInfo.name}
                    />
                    <ContentContainer>
                        <Paper>
                            <Grid container spacing={3}>
                                <Grid item xs={12} sm={6}>
                                    <Box>
                                        <List>
                                            <ListItem data-testid="child-device-name">
                                                <Box width={400}>
                                                    <Typography
                                                        variant="h6"
                                                        color="primary"
                                                        style={{ wordWrap: 'break-word' }}
                                                    >
                                                        {pxChildDeviceInfo.name}
                                                    </Typography>
                                                </Box>

                                                <ListItemSecondaryAction>
                                                    <ListItemIcon
                                                        data-testid="device-status-icon"
                                                        style={{ display: 'block' }}
                                                    >
                                                        {deviceStatus ? (
                                                            <StatusTag
                                                                status={Status.Online}
                                                                style={{ marginRight: 30 }}
                                                            />
                                                        ) : (
                                                            <StatusTag
                                                                status={Status.Offline}
                                                                style={{ marginRight: 30 }}
                                                            />
                                                        )}
                                                    </ListItemIcon>
                                                </ListItemSecondaryAction>
                                            </ListItem>
                                            <ListItem data-testid="device-type" className={classes.margin8}>
                                                <LabelWithSubTitle
                                                    title={pxChildDeviceInfo.deviceProfileName}
                                                    subTitle={'Supported Device Type'}
                                                />
                                            </ListItem>

                                            <ListItem data-testid="communicationType" className={classes.margin8}>
                                                <LabelWithSubTitle
                                                    title={pxChildDeviceInfo.communicationType}
                                                    subTitle={'Communication Type'}
                                                />
                                            </ListItem>

                                            {/* <ListItem data-testid="family" className={classes.margin8}>
                                                <LabelWithSubTitle
                                                     title={pxChildDeviceInfo.mac}
                                                     subTitle={'Family'}
                                                />
                                            </ListItem> */}
                                            {pxChildDeviceInfo.communicationType.toLowerCase() === TCP_TYPE && (
                                                <>
                                                    <ListItem data-testid="deviceIp" className={classes.margin8}>
                                                        <LabelWithSubTitle
                                                            title={pxChildDeviceInfo.deviceIP}
                                                            subTitle={'Device IP'}
                                                        />
                                                    </ListItem>
                                                    <ListItem data-testid="deviceAddress" className={classes.margin8}>
                                                        <LabelWithSubTitle
                                                            title={pxChildDeviceInfo.deviceAddress}
                                                            subTitle={'Device Address'}
                                                        />
                                                    </ListItem>
                                                    <ListItem data-testid="serial" className={classes.margin8}>
                                                        <LabelWithSubTitle
                                                            title={pxChildDeviceInfo.serial}
                                                            subTitle={'Serial'}
                                                        />
                                                    </ListItem>
                                                    <ListItem data-testid="mac" className={classes.margin8}>
                                                        <LabelWithSubTitle
                                                            title={pxChildDeviceInfo.mac}
                                                            subTitle={'Mac'}
                                                        />
                                                    </ListItem>

                                                    <ListItem data-testid="ipport" className={classes.margin8}>
                                                        <LabelWithSubTitle
                                                            title={pxChildDeviceInfo.port}
                                                            subTitle={'IP Port'}
                                                        />
                                                    </ListItem>
                                                    <ListItem data-testid="scanRate" className={classes.margin8}>
                                                        <LabelWithSubTitle
                                                            title={`${pxChildDeviceInfo.scanRate} ms`}
                                                            subTitle={'Scan Rate'}
                                                        />
                                                    </ListItem>
                                                </>
                                            )}
                                            {pxChildDeviceInfo.communicationType.toLowerCase() === SERIAL_TYPE && (
                                                <>
                                                    <ListItem data-testid="deviceAddress" className={classes.margin8}>
                                                        <LabelWithSubTitle
                                                            title={pxChildDeviceInfo.deviceAddress}
                                                            subTitle={'Device Address'}
                                                        />
                                                    </ListItem>

                                                    <ListItem data-testid="baudrate" className={classes.margin8}>
                                                        <LabelWithSubTitle
                                                            title={pxChildDeviceInfo.baudRate}
                                                            subTitle={'Baud Rate'}
                                                        />
                                                    </ListItem>

                                                    <ListItem data-testid="dataBits" className={classes.margin8}>
                                                        <LabelWithSubTitle
                                                            title={pxChildDeviceInfo.dataBits}
                                                            subTitle={'Data Bits'}
                                                        />
                                                    </ListItem>
                                                    <ListItem data-testid="scanRate" className={classes.margin8}>
                                                        <LabelWithSubTitle
                                                            title={`${pxChildDeviceInfo.scanRate} ms`}
                                                            subTitle={'Scan Rate'}
                                                        />
                                                    </ListItem>
                                                    <ListItem data-testid="parity" className={classes.margin8}>
                                                        <LabelWithSubTitle
                                                            title={pxChildDeviceInfo.parity}
                                                            subTitle={'Parity'}
                                                        />
                                                    </ListItem>
                                                    <ListItem data-testid="stopBits" className={classes.margin8}>
                                                        <LabelWithSubTitle
                                                            title={pxChildDeviceInfo.stopBits}
                                                            subTitle={'Stop Bits'}
                                                        />
                                                    </ListItem>
                                                    <ListItem data-testid="comPort" className={classes.margin8}>
                                                        <LabelWithSubTitle
                                                            title={pxChildDeviceInfo.comPort}
                                                            subTitle={'Com Port'}
                                                        />
                                                    </ListItem>
                                                </>
                                            )}
                                        </List>
                                    </Box>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Box style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                        <List>
                                            <ListItem data-testid="device-details">
                                                <Button
                                                    style={{ marginRight: 15, width: 300 }}
                                                    variant="contained"
                                                    color="primary"
                                                    startIcon={<EditIcon />}
                                                    data-testid="edit-action"
                                                    onClick={handleEditDialogOpen}
                                                >
                                                    Edit Device
                                                </Button>
                                            </ListItem>
                                            <ListItem>
                                                <Button
                                                    style={{
                                                        width: 300,
                                                        color: PXBColors.red[500],
                                                        borderColor: PXBColors.gray[100],
                                                    }}
                                                    startIcon={<DeleteForever />}
                                                    variant="outlined"
                                                    color="inherit"
                                                    data-testid="delete-action"
                                                    onClick={(): void => {
                                                        onDelete();
                                                    }}
                                                >
                                                    Delete Forever
                                                </Button>
                                            </ListItem>
                                        </List>
                                    </Box>
                                </Grid>
                            </Grid>
                        </Paper>
                        {
                            <Grid item xs={12} sm={6}>
                                {props.starfishDeviceDetails && (
                                    <DeleteConfirmModal
                                        open={openDelete}
                                        onClose={handleClose}
                                        name={props.starfishDeviceDetails.name}
                                        handleDelete={handleDelete}
                                        navigateFn={navigateToDeviceProfile}
                                    />
                                )}
                            </Grid>
                        }
                    </ContentContainer>
                </>
            )}
        </>
    );
};
